import { useEffect, useMemo, useState } from "react";

import { ChevronDownIcon, InfoIcon, SearchIcon } from "@chakra-ui/icons";
import {
  Avatar,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
  VStack
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";

import PhAnalysisTab from "~/components/health-metrics/PhAnalysisTab";
import PhHistoryTab from "~/components/health-metrics/PhHistoryTab";
import Rbti from "~/components/health-metrics/Rbti";
import Vitals from "~/components/health-metrics/Vitals";
import { useAppState } from "~/hooks/useAppState";
import { useUserHome } from "~/hooks/useUserHome";
import { Member, useAppStore } from "~/store";

// Using the same mock data as in Vitals.tsx
// In a real app, this would come from an API or be moved to a shared location
const mockMembers: Member[] = [
  {
    id: 1,
    name: "John Doe",
    age: 42,
    gender: "M",
    profileImageUrl: "https://api.dicebear.com/7.x/bottts/svg?seed=john"
  },
  {
    id: 2,
    name: "Jane Smith",
    age: 38,
    gender: "F",
    profileImageUrl: "https://api.dicebear.com/7.x/bottts/svg?seed=jane"
  },
  {
    id: 3,
    name: "Alex Johnson",
    age: 12,
    gender: "M",
    profileImageUrl: "https://api.dicebear.com/7.x/bottts/svg?seed=alex"
  },
  {
    id: 4,
    name: "Emily Clark",
    age: 28,
    gender: "F",
    profileImageUrl: "https://api.dicebear.com/7.x/bottts/svg?seed=emily"
  },
  {
    id: 5,
    name: "Michael Brown",
    age: 45,
    gender: "M",
    profileImageUrl: "https://api.dicebear.com/7.x/bottts/svg?seed=michael"
  }
];

export const HealthMetricsPage = () => {
  const { currentMember } = useAppState("currentMember");
  const { userHomeData } = useAppState("userHomeData");
  const { groupMembers } = useUserHome();
  const setCurrentMember = useAppStore((state) => state.setCurrentMember);
  const [searchQuery, setSearchQuery] = useState("");

  const location = useLocation();
  const navigate = useNavigate();

  // Extract members from userHomeData.groupMembers
  const members = useMemo(() => {
    if (!groupMembers) return [];
    return groupMembers.map((gm) => gm.member);
  }, [groupMembers]);

  // Determine the tab index based on the URL path
  const getTabIndexFromPath = () => {
    const path = location.pathname;
    if (path.includes("/health-metrics/rbti")) return 0;
    if (path.includes("/health-metrics/ph-analysis")) return 1;
    if (path.includes("/health-metrics/history")) return 2;
    if (path.includes("/health-metrics/vitals")) return 3;
    return 1; // Default to Ph Analysis tab if just "/health-metrics"
  };

  const [tabIndex, setTabIndex] = useState(getTabIndexFromPath());

  // Update tab index when the URL changes
  useEffect(() => {
    setTabIndex(getTabIndexFromPath());
  }, [location.pathname]);

  // Redirect from base path to default tab
  useEffect(() => {
    if (location.pathname === "/health-metrics") {
      navigate("/health-metrics/ph-analysis");
    }
  }, [location.pathname, navigate]);

  // Filter members based on search query
  const filteredMembers = useMemo(() => {
    return Array.isArray(members)
      ? members.filter((member) => member.name.toLowerCase().includes(searchQuery.toLowerCase()))
      : [];
  }, [members, searchQuery]);

  // Color mode values
  const memberCardBg = useColorModeValue("gray.50", "gray.700");
  const memberCardBorder = useColorModeValue("gray.200", "gray.600");
  const memberNameColor = useColorModeValue("gray.800", "white");
  const memberInfoColor = useColorModeValue("gray.600", "gray.300");
  const alertBg = useColorModeValue("blue.50", "blue.900");
  const alertTextColor = useColorModeValue("blue.600", "blue.200");

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Reset search when menu closes
  const handleMenuClose = () => {
    setTimeout(() => setSearchQuery(""), 200);
  };

  const handleTabChange = (index: number) => {
    setTabIndex(index);

    // Update the URL based on the selected tab
    const paths = [
      "/health-metrics/rbti",
      "/health-metrics/ph-analysis",
      "/health-metrics/history",
      "/health-metrics/vitals"
    ];

    navigate(paths[index]);
  };

  return (
    <Box minH="100vh">
      <Container maxW="container.xl" py={4}>
        <Stack spacing={6}>
          <Flex direction={{ base: "column", md: "row" }} justify="space-between" align="center" gap={4}>
            <Heading as="h1" size="xl">
              Health Metrics
            </Heading>

            {currentMember ? (
              <HStack
                spacing={3}
                bg={memberCardBg}
                p={2}
                borderRadius="md"
                boxShadow="sm"
                borderWidth="1px"
                borderColor={memberCardBorder}
                width="320px"
              >
                <Avatar size="md" name={currentMember.name} src={currentMember.profileImageUrl} />
                <VStack align="start" spacing={0} flex="1" minWidth="0">
                  <Text fontWeight="bold" color={memberNameColor} noOfLines={1}>
                    {currentMember.name}
                  </Text>
                  <Text fontSize="sm" color={memberInfoColor} noOfLines={1}>
                    ID: {currentMember.id} • {currentMember.age} years •{" "}
                    {currentMember.gender === "M" ? "Male" : currentMember.gender === "F" ? "Female" : "Other"}
                  </Text>
                </VStack>
                <Menu onClose={handleMenuClose}>
                  <MenuButton as={Button} size="sm" rightIcon={<ChevronDownIcon />}>
                    Change
                  </MenuButton>
                  <MenuList>
                    <Box px={4} pb={2}>
                      <InputGroup size="sm">
                        <InputLeftElement pointerEvents="none">
                          <SearchIcon color="gray.400" />
                        </InputLeftElement>
                        <Input
                          placeholder="Search members..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          autoFocus
                        />
                      </InputGroup>
                    </Box>
                    <MenuDivider />
                    {filteredMembers.length > 0 ? (
                      filteredMembers.map((member) => (
                        <MenuItem key={member.id} onClick={() => setCurrentMember(member)}>
                          <Flex align="center">
                            <Avatar size="xs" name={member.name} src={member.profileImageUrl} mr={2} />
                            <Text>{member.name}</Text>
                          </Flex>
                        </MenuItem>
                      ))
                    ) : (
                      <Box px={4} py={2}>
                        <Text fontSize="sm" color="gray.500">
                          No members found
                        </Text>
                      </Box>
                    )}
                  </MenuList>
                </Menu>
              </HStack>
            ) : (
              <Menu onClose={handleMenuClose}>
                <MenuButton as={Button} rightIcon={<ChevronDownIcon />} colorScheme="blue">
                  Select a Member
                </MenuButton>
                <MenuList>
                  <Box px={4} pb={2}>
                    <InputGroup size="sm">
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.400" />
                      </InputLeftElement>
                      <Input
                        placeholder="Search members..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        autoFocus
                      />
                    </InputGroup>
                  </Box>
                  <MenuDivider />
                  {filteredMembers.length > 0 ? (
                    filteredMembers.map((member) => (
                      <MenuItem key={member.id} onClick={() => setCurrentMember(member)}>
                        <Flex align="center">
                          <Avatar size="xs" name={member.name} src={member.profileImageUrl} mr={2} />
                          <Text>{member.name}</Text>
                        </Flex>
                      </MenuItem>
                    ))
                  ) : (
                    <Box px={4} py={2}>
                      <Text fontSize="sm" color="gray.500">
                        No members found
                      </Text>
                    </Box>
                  )}
                </MenuList>
              </Menu>
            )}
          </Flex>

          <Flex align="center" gap={2} color="gray.600">
            <InfoIcon />
            <Text>This is not medical advice</Text>
          </Flex>

          {!currentMember && (
            <Box bg={alertBg} p={4} borderRadius="md">
              <Text color={alertTextColor}>Please select a member above to view their health metrics data.</Text>
            </Box>
          )}

          <Tabs variant="enclosed" index={tabIndex} onChange={handleTabChange}>
            <TabList>
              <Tab>RBTI</Tab>
              <Tab>Ph Analysis</Tab>
              <Tab isDisabled={!currentMember}>History</Tab>
              <Tab isDisabled={!currentMember}>Vitals</Tab>
            </TabList>
            <TabPanels>
              <TabPanel>
                <Rbti />
              </TabPanel>
              <TabPanel>
                <PhAnalysisTab />
              </TabPanel>
              <TabPanel>
                <PhHistoryTab />
              </TabPanel>
              <TabPanel>
                <Vitals />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Stack>
      </Container>
    </Box>
  );
};

export default HealthMetricsPage;
